import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmtINR } from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ShieldCheck, CheckCircle2 } from "lucide-react";

export default function Checkout() {
  const { session } = useParams();
  const nav = useNavigate();
  const [booking, setBooking] = useState(null);
  const [purchaser, setPurchaser] = useState({ name: "", email: "", phone: "" });
  const [billing, setBilling] = useState({ name: "", address: "", city: "", state: "Andhra Pradesh", pincode: "", gstin: "" });
  const [attendees, setAttendees] = useState([]);
  const [showBilling, setShowBilling] = useState(false);
  const [paying, setPaying] = useState(false);
  const [rzp, setRzp] = useState(null);

  useEffect(() => {
    api.get(`/bookings/by-id/${session}`).then((r) => onLoaded(r.data)).catch(async () => {
      try { const r = await api.get(`/bookings/${session}`); onLoaded(r.data); } catch { toast.error("Booking session not found"); }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const onLoaded = (b) => {
    setBooking(b);
    // Load event for custom_questions
    if (b.event_slug) {
      api.get(`/events/${b.event_slug}`).then((r) => {
        setBooking((prev) => ({ ...prev, custom_questions: r.data.custom_questions || [] }));
      }).catch(() => {});
    }
    const arr = [];
    (b.line_items || []).forEach((li) => {
      for (let i = 0; i < li.qty; i++) arr.push({ name: "", email: "", phone: "", tier_id: li.tier_id, tier_name: li.tier_name, answers: {} });
    });
    setAttendees(arr);
  };

  const total = booking?.total_paise || 0;

  useEffect(() => {
    if (!document.getElementById("rzp-script")) {
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.id = "rzp-script";
      document.body.appendChild(s);
    }
  }, []);

  const submit = async () => {
    if (!purchaser.name || !purchaser.email) return toast.error("Please add your name & email");
    for (const a of attendees) {
      if (!a.name || !a.email) return toast.error("All attendee details are required");
      for (const cq of (booking?.custom_questions || [])) {
        if (cq.required && !(a.answers?.[cq.key] || "").trim()) return toast.error(`Please answer: ${cq.label}`);
      }
    }
    setPaying(true);
    try {
      const r = await api.post("/bookings/checkout", {
        booking_id: booking.id,
        purchaser_name: purchaser.name,
        purchaser_email: purchaser.email,
        purchaser_phone: purchaser.phone,
        attendees,
        billing: showBilling ? billing : null,
      });
      if (r.data.status === "free_confirmed") {
        toast.success("Booking confirmed");
        return nav(`/booking/${r.data.reference}`);
      }
      const info = r.data.razorpay;
      setRzp(info);
      if (info.mock) {
        // Simulated: verify with mock signature
        const v = await api.post("/bookings/verify", {
          booking_id: booking.id,
          razorpay_order_id: info.order_id,
          razorpay_payment_id: `pay_mock_${Date.now()}`,
          razorpay_signature: `mock_sig_${info.order_id}`,
        });
        toast.success("Payment successful (test mode)");
        return nav(`/booking/${v.data.reference}`);
      }
      // Real Razorpay
      const opts = {
        key: info.key_id,
        amount: info.amount,
        currency: info.currency,
        order_id: info.order_id,
        name: "Common Ground Collective",
        description: booking.event_title,
        prefill: { name: purchaser.name, email: purchaser.email, contact: purchaser.phone },
        handler: async (resp) => {
          try {
            const v = await api.post("/bookings/verify", {
              booking_id: booking.id,
              razorpay_order_id: info.order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            nav(`/booking/${v.data.reference}`);
          } catch (e) { toast.error("Payment verification failed"); }
        },
        modal: { ondismiss: () => setPaying(false) },
        theme: { color: "#C2410C" },
      };
      const R = new window.Razorpay(opts);
      R.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
    } finally { setPaying(false); }
  };

  if (!booking) return <PublicLayout><div className="p-10 text-center">Loading checkout…</div></PublicLayout>;

  return (
    <PublicLayout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="eyebrow">Checkout</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold mt-2">{booking.event_title}</h1>
        {rzp?.mock && (
          <div className="mt-4 text-xs px-3 py-2 rounded bg-amber-100 text-amber-900 border border-amber-300 inline-block">
            Razorpay is in test/mock mode — payment will be simulated
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-8">
          <div className="lg:col-span-7 space-y-8">
            <section className="cgc-card p-6">
              <div className="eyebrow mb-4">Purchaser</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Full name" testid="purchaser-name" value={purchaser.name} onChange={(v) => setPurchaser({ ...purchaser, name: v })} />
                <Field label="Email" testid="purchaser-email" value={purchaser.email} onChange={(v) => setPurchaser({ ...purchaser, email: v })} />
                <Field label="Phone" testid="purchaser-phone" value={purchaser.phone} onChange={(v) => setPurchaser({ ...purchaser, phone: v })} />
              </div>
            </section>

            <section className="cgc-card p-6">
              <div className="eyebrow mb-4">Attendees</div>
              <div className="space-y-6">
                {attendees.map((a, i) => (
                  <div key={i} className="border-l-2 border-[color:var(--brand-terracotta)] pl-4">
                    <div className="text-sm text-stone-500 mb-2">Attendee #{i + 1} · {a.tier_name}</div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <Field label="Name" testid={`att-name-${i}`} value={a.name} onChange={(v) => { const n=[...attendees]; n[i].name=v; setAttendees(n); }} />
                      <Field label="Email" testid={`att-email-${i}`} value={a.email} onChange={(v) => { const n=[...attendees]; n[i].email=v; setAttendees(n); }} />
                      <Field label="Phone" testid={`att-phone-${i}`} value={a.phone} onChange={(v) => { const n=[...attendees]; n[i].phone=v; setAttendees(n); }} />
                    </div>
                    {(booking?.custom_questions || []).length > 0 && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                        {(booking?.custom_questions || []).map((cq) => (
                          <div key={cq.key}>
                            <Label className="text-xs">{cq.label}{cq.required ? " *" : ""}</Label>
                            {cq.type === "select" && Array.isArray(cq.options) ? (
                              <select
                                data-testid={`att-q-${i}-${cq.key}`}
                                className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm bg-background"
                                value={a.answers?.[cq.key] || ""}
                                onChange={(e) => { const n=[...attendees]; n[i].answers = { ...(n[i].answers||{}), [cq.key]: e.target.value }; setAttendees(n); }}
                              >
                                <option value="">Select…</option>
                                {cq.options.map((op) => <option key={op} value={op}>{op}</option>)}
                              </select>
                            ) : (
                              <Input
                                data-testid={`att-q-${i}-${cq.key}`}
                                value={a.answers?.[cq.key] || ""}
                                onChange={(e) => { const n=[...attendees]; n[i].answers = { ...(n[i].answers||{}), [cq.key]: e.target.value }; setAttendees(n); }}
                                className="mt-1"
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>

            <section className="cgc-card p-6">
              <label className="flex items-center gap-2">
                <input type="checkbox" data-testid="billing-toggle" checked={showBilling} onChange={(e) => setShowBilling(e.target.checked)} />
                <span className="text-sm">I need a GST invoice</span>
              </label>
              {showBilling && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  <Field label="Legal name" testid="bill-name" value={billing.name} onChange={(v) => setBilling({ ...billing, name: v })} />
                  <Field label="GSTIN" testid="bill-gstin" value={billing.gstin} onChange={(v) => setBilling({ ...billing, gstin: v.toUpperCase() })} />
                  <Field label="Address" testid="bill-address" value={billing.address} onChange={(v) => setBilling({ ...billing, address: v })} />
                  <Field label="City" testid="bill-city" value={billing.city} onChange={(v) => setBilling({ ...billing, city: v })} />
                  <Field label="State" testid="bill-state" value={billing.state} onChange={(v) => setBilling({ ...billing, state: v })} />
                  <Field label="Pincode" testid="bill-pin" value={billing.pincode} onChange={(v) => setBilling({ ...billing, pincode: v })} />
                </div>
              )}
            </section>
          </div>

          <aside className="lg:col-span-5">
            <div className="cgc-card p-6 sticky top-20">
              <div className="eyebrow mb-4">Order summary</div>
              <ul className="space-y-2 text-sm">
                {booking.line_items.map((li, i) => (
                  <li key={i} className="flex justify-between">
                    <span>{li.tier_name} × {li.qty}</span>
                    <span className="font-mono">{fmtINR(li.subtotal_paise)}</span>
                  </li>
                ))}
              </ul>
              <div className="border-t border-border my-4" />
              <Row label="Subtotal" value={fmtINR(booking.subtotal_paise)} />
              {booking.discount_paise > 0 && <Row label={`Discount (${booking.coupon_code})`} value={`− ${fmtINR(booking.discount_paise)}`} />}
              {booking.checkout_charge_paise > 0 && <Row label="Checkout charge (2%)" value={fmtINR(booking.checkout_charge_paise)} />}
              <div className="border-t border-border my-3" />
              <div className="flex justify-between items-baseline">
                <span className="font-semibold">Total</span>
                <span className="font-display text-2xl font-bold" data-testid="checkout-total">{fmtINR(total)}</span>
              </div>
              <Button data-testid="pay-btn" onClick={submit} disabled={paying} className="w-full mt-5 h-12 btn-brand">
                {paying ? <><Loader2 className="animate-spin mr-2" size={16}/>Processing…</> : total === 0 ? "Confirm free registration" : "Pay securely"}
              </Button>
              <div className="mt-3 text-[11px] text-stone-500 flex items-center gap-2"><ShieldCheck size={12}/> Signature-verified · No card data stored</div>
            </div>
          </aside>
        </div>
      </div>
    </PublicLayout>
  );
}

function Row({ label, value }) {
  return <div className="flex justify-between text-sm"><span className="text-stone-600 dark:text-stone-400">{label}</span><span className="font-mono">{value}</span></div>;
}
function Field({ label, testid, value, onChange }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1" />
    </div>
  );
}
